// ---------------------------------------------------------------------------
// Cookie helpers (no js-cookie dependency)
// ---------------------------------------------------------------------------

function cookieSet(name, value, days) {
  var expires = '';
  if (days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + d.toUTCString();
  }
  document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/';
}

function cookieGet(name) {
  var needle = name + '=';
  var parts  = document.cookie.split(';');
  for (var i = 0; i < parts.length; i++) {
    var part = parts[i].trimStart();
    if (part.indexOf(needle) === 0) {
      return decodeURIComponent(part.slice(needle.length));
    }
  }
  return null;
}

/**
 * CPZone — streamlined postal-code / delivery-zone resolver.
 *
 * Replaces the scattered matchCP() / submitForm() / updateTimePicker()
 * postal-code logic with small, composable, globally-accessible functions.
 *
 * COMPAT NOTE: this writes the SAME cookies and window.theme keys the
 * legacy code reads (zoneArrayCookie, cpPopupCookie, window.theme.tag/
 * area/zone/cp/express_delivery), so it can run side-by-side with the
 * old IIFEs during the cart rewrite. Once the rewrite is done, the old
 * matchCP/submitForm/updateTimePicker zone-resolution code can be deleted
 * and everything can call CPZone directly instead.
 *
 * No external cookie library required — uses its own cookie helpers.
 *
 * Requires: window.theme.CP_matix to be populated (array of zone definitions),
 * same as today.
 *
 * Expected CP_matix entry shape:
 *   {
 *     tag: 'madrid' | 'rest' | ...,
 *     name: 'Comunidad de Madrid',
 *     zone: '1' | '2',
 *     express_delivery: 'true' | 'false',
 *     cp_list: '28001,28002,28003,...'   // comma-separated, exact CPs
 *   }
 */
(function (window) {
  'use strict';

  var COOKIE_AREA = 'zoneArrayCookie';
  var COOKIE_CP   = 'cpPopupCookie';
  var COOKIE_CP_SET = 'cpSetCookie';
  var COOKIE_DAYS = 5;

  // ---------------------------------------------------------------------------
  // Island / restricted-province detection
  // ---------------------------------------------------------------------------

  // Provinces served only by special (non-standard) logistics — kept as a
  // named constant instead of an inline magic regex.
  var ISLAND_PROVINCE_PREFIXES = ['07', '35', '38', '51', '52'];
  var ISLAND_CP_REGEX = new RegExp('^(' + ISLAND_PROVINCE_PREFIXES.join('|') + ')');

  /**
   * True if a CP belongs to a province with restricted/special shipping
   * (Balearics, Canarias, Ceuta, Melilla).
   */
  function isIslandCP(cp) {
    return ISLAND_CP_REGEX.test(String(cp || ''));
  }

  // ---------------------------------------------------------------------------
  // Zone resolution (pure, no side effects)
  // ---------------------------------------------------------------------------

  /**
   * Pure lookup: given a CP and a CP_matix array, returns:
   *   - a single matching zone entry  (exact match)
   *   - an array of entries           (ambiguous — caller shows a picker)
   *   - the "rest" fallback entry     (no match found)
   *   - null                          (no match AND no "rest" entry exists)
   *
   * Uses exact match against cp_list (not substring), fixing the legacy
   * loose indexOf() bug in the original matchCP().
   */
  function findZone(cp, cpMatix) {
    cpMatix = cpMatix || (window.theme && window.theme.CP_matix) || [];
    var clean = String(cp || '').trim();
    if (!clean) return null;

    var matches = [];
    for (var i = 0; i < cpMatix.length; i++) {
      var entry = cpMatix[i];
      if (!entry.cp_list) continue;
      var list = entry.cp_list.split(',').map(function (c) { return c.trim(); });
      if (list.indexOf(clean) !== -1) {
        matches.push(entry);
      }
    }

    if (matches.length === 0) {
      return cpMatix.find(function (e) { return e.tag === 'rest'; }) || null;
    }
    return matches[0];
  }

  /**
   * Like findZone, but always returns a single entry — collapses an
   * ambiguous array to its first candidate. Useful when you just need
   * a zone and don't want to handle the multi-match case.
   */
  function resolveZone(cp, cpMatix) {
    var result = findZone(cp, cpMatix);
    if (Array.isArray(result)) result = result[0];
    return result || null;
  }

  // ---------------------------------------------------------------------------
  // Stored-state accessors
  // ---------------------------------------------------------------------------

  /**
   * Returns the currently stored zone entry from cookies, or null.
   * No CP_matix lookup needed — reads whatever was last committed.
   */
  function getStoredZone() {
    var raw = cookieGet(COOKIE_AREA);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  /**
   * Returns the raw CP string that was last committed, or null.
   */
  function getStoredCP(mode = null) {
    let storedCP = cookieGet(COOKIE_CP);
    switch (mode) {
      case 'if-not-set':    
        if (!isCPSet()) {
          storedCP = '';
        }
        break;
    }
    return storedCP;
  }

  // ---------------------------------------------------------------------------
  // Side-effectful zone application
  // ---------------------------------------------------------------------------

  /**
   * Commits a resolved zone entry as the active zone.
   * This is the ONLY function in the module with side effects:
   * writes cookies, updates window.theme, toggles body classes,
   * and fires a 'cpzone:change' event.
   *
   * @param {Object} zoneEntry - one CP_matix entry (from findZone/resolveZone)
   * @param {String} cp        - the raw postal code that produced it
   */
  function applyZone(zoneEntry, cp) {
    if (!zoneEntry) return;

    cookieSet(COOKIE_AREA, JSON.stringify(zoneEntry), COOKIE_DAYS);
    cookieSet(COOKIE_CP, cp || '', COOKIE_DAYS);

    window.theme = window.theme || {};
    window.theme.tag              = zoneEntry.tag;
    window.theme.area             = zoneEntry.name;
    window.theme.zone             = zoneEntry.zone;
    window.theme.cp               = cp || '';
    window.theme.express_delivery = zoneEntry.express_delivery;

    var body = document.body;
    body.classList.add('visible');
    body.classList.remove('madrid', 'rest', 'normal-blocks', 'express-blocks');
    body.classList.add(zoneEntry.tag === 'madrid' ? 'madrid' : 'rest');
    body.classList.add(zoneEntry.express_delivery === 'true' ? 'express-blocks' : 'normal-blocks');
    body.setAttribute('data-user-city', zoneEntry.tag);

    document.dispatchEvent(new CustomEvent('cpzone:change', {
      detail: { zoneEntry: zoneEntry, cp: cp }
    }));
  }

  function setCPSetCookie(value) {
    cookieSet(COOKIE_CP_SET, value, COOKIE_DAYS);
  }

  function isCPSet() {
    let cookie = cookieGet(COOKIE_CP_SET);
    return cookie === 'is-set';
  }

  /**
   * One-call convenience: resolve a CP and immediately apply it.
   * Returns the resolved zone entry so callers can inspect it.
   */
  function setZoneFromCP(cp, cpMatix) {
    var zoneEntry = resolveZone(cp, cpMatix);
    applyZone(zoneEntry, cp);
    return zoneEntry;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  window.CPZone = {
    // Query
    isIslandCP:   isIslandCP,
    findZone:     findZone,     // pure, returns entry | array | null
    resolveZone:  resolveZone,  // pure, always single entry or null

    // Stored state
    getStoredZone: getStoredZone,
    getStoredCP:   getStoredCP,

    setCPSetCookie: setCPSetCookie,
    isCPSet: isCPSet,

    // Apply (side effects)
    applyZone:    applyZone,
    setZoneFromCP: setZoneFromCP,
  };

})(window);